// WP50 shell: accordion menu, collapsible rail, theme switch, request progress bar. Keyboard-repeated actions never animate.
import { readTheme, saveTheme, type Theme } from "./theme";

const shell = () => document.getElementById("app-shell");
const narrow = window.matchMedia("(max-width: 900px)");
const RAIL_KEY = "dhuwani.sidebar";

function setRail(on: boolean): void {
  shell()?.classList.toggle("sidebar-collapsed", on);
  try {
    if (on) localStorage.setItem(RAIL_KEY, "rail");
    else localStorage.removeItem(RAIL_KEY);
  } catch {
    // private window: the choice lasts for this page only
  }
}

function railOn(): boolean {
  try { return localStorage.getItem(RAIL_KEY) === "rail"; } catch { return false; }
}

/** The phone drawer: the menu button says whether it is open (aria-expanded). */
function setDrawer(open: boolean): void {
  shell()?.classList.toggle("sidebar-open", open);
  document.getElementById("cb-toggle-sidebar")?.setAttribute("aria-expanded", String(open));
}

function markTheme(theme: Theme): void {
  document.querySelectorAll<HTMLButtonElement>("[data-theme-set]").forEach((b) =>
    b.setAttribute("aria-pressed", String(b.dataset.themeSet === theme)));
}

/** Opening a group closes its open siblings, so only one branch of the menu is open at a level. */
function toggleGroup(button: HTMLElement): void {
  const item = button.parentElement;
  if (!item) return;
  const opening = !item.classList.contains("is-open");
  if (opening) {
    item.parentElement?.querySelectorAll<HTMLElement>(":scope > .is-open").forEach((sibling) => {
      sibling.classList.remove("is-open");
      sibling.querySelector(":scope > [data-nav-toggle]")?.setAttribute("aria-expanded", "false");
    });
    if (!narrow.matches && shell()?.classList.contains("sidebar-collapsed")) setRail(false);
  }
  item.classList.toggle("is-open", opening);
  button.setAttribute("aria-expanded", String(opening));
}

document.addEventListener("click", (ev) => {
  const target = ev.target instanceof Element ? ev.target : null;
  if (!target) return;
  const toggle = target.closest<HTMLElement>("[data-nav-toggle]");
  if (toggle) { ev.preventDefault(); toggleGroup(toggle); return; }
  if (target.closest("[data-sidebar-collapse]")) { setRail(!shell()?.classList.contains("sidebar-collapsed")); return; }
  if (target.closest("#cb-toggle-sidebar")) {
    if (narrow.matches) setDrawer(!shell()?.classList.contains("sidebar-open"));
    else setRail(!shell()?.classList.contains("sidebar-collapsed"));
    return;
  }
  const theme = target.closest<HTMLElement>("[data-theme-set]");
  if (theme) { const t = theme.dataset.themeSet as Theme; saveTheme(t); markTheme(t); return; }
  if (target.closest("[data-open-palette]")) {
    document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", ctrlKey: true, bubbles: true }));
    return;
  }
  const close = target.closest("[data-close-dialog]");
  if (close) { close.closest("dialog")?.close(); return; }
  // a tap outside the drawer closes it on phones and tablets
  if (narrow.matches && target.closest("#main")) setDrawer(false);
});

// Esc closes an open drawer first; capture, so the page's own Esc (back to the list) does not also run.
document.addEventListener("keydown", (ev) => {
  if (ev.key !== "Escape" || !shell()?.classList.contains("sidebar-open") || document.querySelector("dialog[open]")) return;
  ev.preventDefault();
  ev.stopImmediatePropagation();
  setDrawer(false);
}, true);

if (railOn() && !narrow.matches) shell()?.classList.add("sidebar-collapsed");
markTheme(readTheme());

// One quiet bar at the top while htmx talks to the server (several requests at once keep it up until the last ends).
let pending = 0;
const bar = () => document.getElementById("progress");
document.addEventListener("htmx:beforeRequest", () => { pending++; bar()?.classList.add("is-loading"); });
const done = () => { pending = Math.max(0, pending - 1); if (pending === 0) bar()?.classList.remove("is-loading"); };
document.addEventListener("htmx:afterRequest", done);
document.addEventListener("htmx:sendError", done);

// A plain (non-htmx) form post shows the busy state on the button that sent it; htmx requests use .htmx-request instead.
// Bubble phase, so a confirm or validation that cancelled the submit (defaultPrevented) leaves the button alone.
document.addEventListener("submit", (ev) => {
  const button = (ev as SubmitEvent).submitter;
  if (!ev.defaultPrevented && button instanceof HTMLButtonElement && !button.classList.contains("menu-item")) {
    button.setAttribute("aria-busy", "true");
    // shortcut: a post that answers with a file download never leaves the page, so the spinner clears itself after 8s.
    window.setTimeout(() => button.removeAttribute("aria-busy"), 8000);
  }
});
// Back/forward restores the page from cache with the old busy state; clear it.
window.addEventListener("pageshow", () => document.querySelectorAll("button[aria-busy='true']").forEach((b) => b.removeAttribute("aria-busy")));
